import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO
        title="Create Account"
        description="Create your free RED.FLAG account and start tracking expenses, setting budgets, and getting AI-powered financial coaching. ₹200 lifetime upgrade for AI Coach access."
        path="/register"
      />
      <div className="bg-black text-white p-10 flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--alert-red)] flex items-center justify-center font-display font-black">!</div>
          <span className="font-display font-black tracking-tighter text-xl">RED.FLAG</span>
        </div>
        <div>
          <div className="overline text-white/60 mb-4">CREATE ACCOUNT</div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            START <br />
            <span className="text-[var(--alert-red)]">TRACKING.</span>
          </h1>
          <p className="mt-6 font-mono text-sm max-w-md text-white/70">
            Free, private, and fast. Logs expenses. Sets budgets. Tells you when you&apos;ve gone too far.
          </p>
        </div>
        <div className="overline text-white/40">YOUR DATA STAYS YOURS.</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="overline mb-2">CREATE ACCOUNT</div>
          <h2 className="font-display font-black text-4xl tracking-tighter mb-8">Hello, who&apos;s tracking?</h2>

          <label className="block mb-6">
            <div className="overline mb-2">NAME</div>
            <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <label className="block mb-6">
            <div className="overline mb-2">EMAIL</div>
            <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <label className="block mb-6">
            <div className="overline mb-2">PASSWORD (min 6)</div>
            <input data-testid="register-password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>

          {err && (
            <div data-testid="register-error" className="mb-4 border-2 border-[var(--alert-red)] bg-[var(--alert-wash)] text-[var(--alert-red)] p-3 font-mono text-sm">{err}</div>
          )}

          <button data-testid="register-submit" disabled={loading}
            className="w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50">
            {loading ? "CREATING…" : "CREATE ACCOUNT →"}
          </button>

          <div className="mt-6 font-mono text-sm">
            Already have one?{" "}
            <Link to="/login" data-testid="link-to-login" className="underline font-bold">SIGN IN</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
